/**
 * HS256 JWT — minimal, dependency-free, Web-Crypto based.
 * Used for the short-lived session cookie set by /api/v1/auth/telegram.
 */

export interface SessionClaims {
  /** users.id */
  sub: string;
  /** Telegram numeric id (auditability) */
  tid: number;
  /** Role for fast authz before DB load. */
  role: 'user' | 'driver' | 'moderator' | 'admin';
  /** Issued at, seconds. */
  iat: number;
  /** Expiry, seconds. */
  exp: number;
}

export class InvalidTokenError extends Error {
  constructor(reason: string) {
    super(`Invalid session token: ${reason}`);
    this.name = 'InvalidTokenError';
  }
}

const HEADER = base64urlEncode(new TextEncoder().encode(JSON.stringify({ alg: 'HS256', typ: 'JWT' })));

export async function signSession(
  claims: Omit<SessionClaims, 'iat' | 'exp'>,
  secret: string,
  ttlSec: number,
): Promise<{ token: string; exp: number }> {
  const now = Math.floor(Date.now() / 1000);
  const full: SessionClaims = { ...claims, iat: now, exp: now + ttlSec };
  const body = base64urlEncode(new TextEncoder().encode(JSON.stringify(full)));
  const data = `${HEADER}.${body}`;
  const sig = await hmacSha256(secret, data);
  return { token: `${data}.${sig}`, exp: full.exp };
}

export async function verifySession(token: string, secret: string): Promise<SessionClaims> {
  if (!token) throw new InvalidTokenError('empty');
  const parts = token.split('.');
  if (parts.length !== 3) throw new InvalidTokenError('malformed');
  const [headerSeg, bodySeg, sigSeg] = parts as [string, string, string];

  if (headerSeg !== HEADER) throw new InvalidTokenError('unsupported header');
  const expected = await hmacSha256(secret, `${headerSeg}.${bodySeg}`);
  if (!safeEqual(expected, sigSeg)) throw new InvalidTokenError('bad signature');

  let claims: SessionClaims;
  try {
    claims = JSON.parse(new TextDecoder().decode(base64urlDecode(bodySeg))) as SessionClaims;
  } catch {
    throw new InvalidTokenError('bad payload');
  }
  const now = Math.floor(Date.now() / 1000);
  if (typeof claims.exp !== 'number' || claims.exp < now) {
    throw new InvalidTokenError('expired');
  }
  if (typeof claims.sub !== 'string' || typeof claims.tid !== 'number') {
    throw new InvalidTokenError('missing required claims');
  }
  return claims;
}

async function hmacSha256(secret: string, data: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign'],
  );
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(data));
  return base64urlEncode(new Uint8Array(sig));
}

function base64urlEncode(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i] ?? 0);
  return btoa(bin).replace(/=+$/g, '').replaceAll('+', '-').replaceAll('/', '_');
}

function base64urlDecode(s: string): Uint8Array {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  const b64 = (s + pad).replaceAll('-', '+').replaceAll('_', '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function safeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}
